import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Loader2 } from 'lucide-react';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';
import MFASetupPage from './pages/auth/MFASetupPage';
import MFAVerifyPage from './pages/auth/MFAVerifyPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';

// Dashboard Pages
import DashboardPage from './pages/dashboard/DashboardPage';

// Admin Pages
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminSchoolsPage from './pages/admin/AdminSchoolsPage';
import SchoolConfigPage from './pages/admin/SchoolConfigPage';
import SystemAdminsPage from './pages/admin/SystemAdminsPage';
import AllUsersPage from './pages/admin/AllUsersPage';
import AdminChangelogPage from './pages/admin/AdminChangelogPage';
import AdminSubscriptionsPage from './pages/admin/AdminSubscriptionsPage';

// Conference Pages
import ConferenceSetsPage from './pages/conference/ConferenceSetsPage';
import ConferenceSetDetailPage from './pages/conference/ConferenceSetDetailPage';

// Schedule Pages
import ScheduleSetsPage from './pages/schedule/ScheduleSetsPage';
import ScheduleSetDetailPage from './pages/schedule/ScheduleSetDetailPage';

// Teachers Page
import TeachersPage from './pages/teachers/TeachersPage';

// Exports Pages
import ExportsPage from './pages/exports/ExportsPage';
import ExportSetDetailPage from './pages/exports/ExportSetDetailPage';

// Settings Pages
import SchoolSettingsPage from './pages/settings/SchoolSettingsPage';
import ManageUsersPage from './pages/settings/ManageUsersPage';
import ProfilePage from './pages/settings/ProfilePage';

// Public Pages
import ChangelogPage from './pages/ChangelogPage';
import HelpPage from './pages/HelpPage';
import RegistrationPage from './pages/RegistrationPage';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    // Redirect based on role
    if (user.role === 'system_admin' && !user.impersonating) {
      return <Navigate to="/admin/dashboard" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route Component (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading, mfaPending, mfaSetupRequired } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (user && !mfaPending && !mfaSetupRequired) {
    if (user.role === 'system_admin' && !user.impersonating) {
      return <Navigate to="/admin/dashboard" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Root Redirect Component
const RootRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role === 'system_admin' && !user.impersonating) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return <Navigate to="/dashboard" replace />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Root redirect */}
      <Route path="/" element={<RootRedirect />} />

      {/* Auth Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/changelog" element={<ChangelogPage />} />
      <Route path="/help" element={<HelpPage />} />
      <Route path="/register" element={<RegistrationPage />} />
      <Route path="/mfa-setup" element={<MFASetupPage />} />
      <Route path="/mfa-verify" element={<MFAVerifyPage />} />

      {/* Admin Routes */}
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute allowedRoles={['system_admin']}>
            <AdminDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/schools"
        element={
          <ProtectedRoute allowedRoles={['system_admin']}>
            <AdminSchoolsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/schools/:id/config"
        element={
          <ProtectedRoute allowedRoles={['system_admin']}>
            <SchoolConfigPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute allowedRoles={['system_admin']}>
            <AllUsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/system-admins"
        element={
          <ProtectedRoute allowedRoles={['system_admin']}>
            <SystemAdminsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/changelog"
        element={
          <ProtectedRoute allowedRoles={['system_admin']}>
            <AdminChangelogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/subscriptions"
        element={
          <ProtectedRoute allowedRoles={['system_admin']}>
            <AdminSubscriptionsPage />
          </ProtectedRoute>
        }
      />

      {/* School User Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedRoles={['school_admin', 'school_user', 'system_admin']}>
            <DashboardPage />
          </ProtectedRoute>
        }
      />

      {/* Conference Sets */}
      <Route
        path="/conference-sets"
        element={
          <ProtectedRoute allowedRoles={['school_admin', 'school_user', 'system_admin']}>
            <ConferenceSetsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/conference-sets/new"
        element={
          <ProtectedRoute allowedRoles={['school_admin', 'school_user', 'system_admin']}>
            <ConferenceSetsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/conference-sets/:id"
        element={
          <ProtectedRoute allowedRoles={['school_admin', 'school_user', 'system_admin']}>
            <ConferenceSetDetailPage />
          </ProtectedRoute>
        }
      />

      {/* Schedule Sets */}
      <Route
        path="/schedule-sets"
        element={
          <ProtectedRoute allowedRoles={['school_admin', 'school_user', 'system_admin']}>
            <ScheduleSetsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/schedule-sets/new"
        element={
          <ProtectedRoute allowedRoles={['school_admin', 'school_user', 'system_admin']}>
            <ScheduleSetsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/schedule-sets/:id"
        element={
          <ProtectedRoute allowedRoles={['school_admin', 'school_user', 'system_admin']}>
            <ScheduleSetDetailPage />
          </ProtectedRoute>
        }
      />

      {/* Teachers */}
      <Route
        path="/teachers"
        element={
          <ProtectedRoute allowedRoles={['school_admin', 'school_user', 'system_admin']}>
            <TeachersPage />
          </ProtectedRoute>
        }
      />

      {/* Exports */}
      <Route
        path="/exports"
        element={
          <ProtectedRoute allowedRoles={['school_admin', 'school_user', 'system_admin']}>
            <ExportsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/export-sets/:id"
        element={
          <ProtectedRoute allowedRoles={['school_admin', 'school_user', 'system_admin']}>
            <ExportSetDetailPage />
          </ProtectedRoute>
        }
      />

      {/* School Admin Settings */}
      <Route
        path="/school-settings"
        element={
          <ProtectedRoute allowedRoles={['school_admin', 'system_admin']}>
            <SchoolSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-users"
        element={
          <ProtectedRoute allowedRoles={['school_admin', 'system_admin']}>
            <ManageUsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute allowedRoles={['system_admin', 'school_admin', 'school_user']}>
            <ProfilePage />
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to appropriate dashboard */}
      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
